import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  Box,
  Building2,
  Camera,
  Check,
  CheckCircle2,
  CircleDot,
  Copy,
  Database,
  Download,
  Eye,
  EyeOff,
  Fence,
  FileUp,
  Image,
  KeyRound,
  Layers3,
  LoaderCircle,
  MapPin,
  Mountain,
  PencilRuler,
  RefreshCw,
  Route,
  Satellite,
  ShieldCheck,
  Trees,
  Waves,
  X,
} from "lucide-react";
import { BuildCancelledError } from "@map2threejs/core/errors";
import type { SceneEnrichmentProposal } from "@map2threejs/core/enrichment";
import {
  DEFAULT_SCENE_RECIPE,
  migrateSceneRecipe,
  redactSensitiveText,
  serializeSceneRecipe,
} from "@map2threejs/core/recipe";
import { polygonEnvelope, validatePolygonSelection } from "@map2threejs/core/region";
import type {
  BuildProgress,
  BuildStage,
  LatLng,
  RegionSelection,
  SceneBuildResult,
  SceneRecipe,
} from "@map2threejs/core/types";
import { MapTilerProvider } from "@map2threejs/maptiler-provider";
import { DemoMapProvider } from "./demo-provider";
import { loadGoogleMaps } from "./google-maps";
import { estimateBuild, type BuildEstimate } from "./build-estimator";
import { DemoSelector, GoogleSelector } from "./components/GoogleSelector";
import type { LayerVisibility } from "./components/ThreeViewport";
import { WorkflowHeader } from "./components/WorkflowHeader";
import { detectWebGl2Capabilities } from "./webgl-capabilities";

const ThreeViewport = lazy(async () => {
  const module = await import("./components/ThreeViewport");
  return { default: module.ThreeViewport };
});

type AppStage = 1 | 2 | 3 | 4;
type ConnectionState = "idle" | "testing" | "connected" | "error";
type SceneExportKind = "project" | "glb" | "native-json";
type GlbCompression = "none" | "meshopt";
type QualityPreset = "fast" | "balanced" | "detail";
type CameraPreset = "isometric" | "street" | "topographic" | "architectural";
type EnrichmentReview = SceneEnrichmentProposal & { decision: "pending" | "approved" | "rejected" };

const QUALITY_PRESETS: Record<QualityPreset, SceneRecipe["quality"]> = {
  fast: { targetImageryMetersPerPixel: 1, maxTextureSize: 2048, maxRasterTiles: 16 },
  balanced: { ...DEFAULT_SCENE_RECIPE.quality },
  detail: { targetImageryMetersPerPixel: 0.25, maxTextureSize: 4096, maxRasterTiles: 64 },
};

interface ConnectionStatus {
  google: ConnectionState;
  maptiler: ConnectionState;
  googleError?: string;
  maptilerError?: string;
}

const INITIAL_CONNECTIONS: ConnectionStatus = { google: "idle", maptiler: "idle" };
const BUILD_STAGES: BuildStage[] = ["source-discovery", "imagery", "terrain", "buildings", "map-features", "geometry", "finalization"];
const BUILD_LABELS: Record<BuildStage, string> = {
  "source-discovery": "Discover sources",
  imagery: "Satellite imagery",
  terrain: "Terrain elevation",
  buildings: "Building footprints",
  "map-features": "Roads, waterways and barriers",
  geometry: "Scene geometry",
  finalization: "Final checks",
};

function clampCoordinate(value: number, minimum: number, maximum: number, fallback: number): number {
  return Number.isFinite(value) ? Math.max(minimum, Math.min(maximum, value)) : fallback;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  globalThis.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function exportBaseName(recipe: SceneRecipe): string {
  return `map2threejs-${recipe.center.lat.toFixed(6)}-${recipe.center.lng.toFixed(6)}`;
}

function StatusLine({ state, label, detail }: { state: ConnectionState; label: string; detail?: string | undefined }) {
  return (
    <div className={`connection-status status-${state}`}>
      <span className="status-icon" aria-hidden="true">
        {state === "testing" && <LoaderCircle className="spin" size={18} />}
        {state === "connected" && <Check size={18} />}
        {state === "error" && <X size={18} />}
        {state === "idle" && <span className="status-dot" />}
      </span>
      <span><strong>{label}</strong>{detail && <small>{detail}</small>}</span>
    </div>
  );
}

function SecretField({
  id,
  label,
  value,
  onChange,
  placeholder,
}: {
  id: string;
  label: string;
  value: string;
  onChange(value: string): void;
  placeholder: string;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <label className="secret-field" htmlFor={id}>
      <span>{label}</span>
      <div className="secret-input-wrap">
        <KeyRound size={18} aria-hidden="true" />
        <input
          id={id}
          name={id}
          type={visible ? "text" : "password"}
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => onChange(event.target.value)}
        />
        <button type="button" onClick={() => setVisible((current) => !current)} aria-label={`${visible ? "Hide" : "Show"} ${label}`}>
          {visible ? <EyeOff size={19} /> : <Eye size={19} />}
        </button>
      </div>
      <small>Held in memory for this tab only.</small>
    </label>
  );
}

function RecipeImportButton({
  onImport,
  label = "Open scene recipe",
  accept = ".json,application/json",
}: {
  onImport(file: File): Promise<void>;
  label?: string;
  accept?: string;
}) {
  return (
    <label className="button secondary recipe-import-button">
      <FileUp size={18} />
      {label}
      <input
        type="file"
        accept={accept}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) void onImport(file);
        }}
      />
    </label>
  );
}

function ConnectScreen({
  demo,
  googleKey,
  mapTilerKey,
  setGoogleKey,
  setMapTilerKey,
  connections,
  testing,
  testConnections,
  continueToRegion,
  importRecipe,
  recipeImportStatus,
}: {
  demo: boolean;
  googleKey: string;
  mapTilerKey: string;
  setGoogleKey(value: string): void;
  setMapTilerKey(value: string): void;
  connections: ConnectionStatus;
  testing: boolean;
  testConnections(): void;
  continueToRegion(): void;
  importRecipe(file: File): Promise<void>;
  recipeImportStatus?: string | undefined;
}) {
  const connected = connections.google === "connected" && connections.maptiler === "connected";
  return (
    <main className="connect-layout">
      <section className="connect-panel">
        <div className="eyebrow"><Box size={17} /> Real map → Three.js</div>
        <h1>Build a 3D map from a real place</h1>
        <p className="lead">Connect Google Maps and MapTiler, select any area, and turn it into an editable, meter-scaled Three.js scene.</p>
        <p className="connect-use-cases">For interactive websites, game prototypes, spatial visualization, architecture and urban context.</p>
        {demo && (
          <div className="demo-banner">
            <Database size={19} />
            <span><strong>Demo fixture is active.</strong> Explore the complete workflow without provider requests or API keys.</span>
            <a href={import.meta.env.BASE_URL}>Use live map data</a>
          </div>
        )}
        {!demo && (
          <p className="demo-entry">
            Want to explore before connecting providers? <a href={`${import.meta.env.BASE_URL}?demo=1`}>Try the provider-free demo.</a>
          </p>
        )}
        <div className="credential-fields">
          <SecretField
            id="google-api-key"
            label="Google Maps API key"
            value={googleKey}
            onChange={setGoogleKey}
            placeholder={demo ? "Not required in demo mode" : "AIza…"}
          />
          <SecretField
            id="maptiler-api-key"
            label="MapTiler API key"
            value={mapTilerKey}
            onChange={setMapTilerKey}
            placeholder={demo ? "Not required in demo mode" : "Enter restricted key"}
          />
        </div>
        <div className="connection-results" aria-live="polite">
          <StatusLine state={connections.google} label="Google Maps + Places API (New)" detail={connections.googleError} />
          <StatusLine state={connections.maptiler} label="MapTiler TileJSON sources" detail={connections.maptilerError} />
        </div>
        <button className="button secondary full" onClick={testConnections} disabled={testing || (!demo && (!googleKey || !mapTilerKey))}>
          <RefreshCw size={19} className={testing ? "spin" : ""} />
          {testing ? "Testing connections…" : "Test connections"}
        </button>
        <button className="button primary full" onClick={continueToRegion} disabled={!connected}>
          Continue to region <span aria-hidden="true">→</span>
        </button>
        <RecipeImportButton onImport={importRecipe} />
        {recipeImportStatus && <p className="recipe-import-status" role="status">{recipeImportStatus}</p>}
        <p className="privacy-note"><ShieldCheck size={16} /> Keys stay in this browser tab only and are never included in recipes or exported scenes.</p>
      </section>
      <section className="connect-explainer" aria-label="map2threejs product overview">
        <div className="explainer-copy">
          <span className="section-kicker">From map selection to production-ready 3D</span>
          <h2>Turn any real-world area into an editable Three.js scene.</h2>
          <p>Search for a place, select a radius or draw a boundary, then generate terrain, buildings, roads, water and land at real-world scale.</p>
        </div>
        <div className="output-formats" aria-label="Available Three.js outputs">
          <div><strong>Runnable project</strong><span>Vite + TypeScript + Three.js</span></div>
          <div><strong>Portable 3D asset</strong><span>GLB for web, games and 3D tools</span></div>
          <div><strong>Native scene</strong><span>Three.js JSON + regeneration recipe</span></div>
        </div>
        <div className="source-stack product-steps" aria-label="How map2threejs works">
          <article><span className="source-icon google"><MapPin /></span><div><strong>Find a real location</strong><small>Search with Google Maps and inspect satellite context</small></div><span className="source-tag">01</span></article>
          <article><span className="source-icon imagery"><PencilRuler /></span><div><strong>Define the exact area</strong><small>Use a radius or draw and edit a custom boundary</small></div><span className="source-tag">02</span></article>
          <article><span className="source-icon terrain"><Layers3 /></span><div><strong>Generate the 3D map</strong><small>Build terrain, imagery, buildings, roads, water and land</small></div><span className="source-tag">03</span></article>
          <article><span className="source-icon buildings"><Download /></span><div><strong>Export and customize</strong><small>Open the project in Three.js or use the GLB in another tool</small></div><span className="source-tag">04</span></article>
        </div>
        <div className="scale-contract"><Box /><div><strong>Made for real Three.js development</strong><span>1 unit = 1 m · coordinates, layers and attribution included</span></div></div>
        <p className="accuracy-note">Provider-faithful visualization for digital experiences and prototyping. Not survey-grade geometry or Google Earth photogrammetry.</p>
      </section>
    </main>
  );
}

function SelectionSidebar({
  center,
  radius,
  selectionMode,
  polygonVertexCount,
  polygonAreaSquareMeters,
  polygonEnvelopeRadius,
  polygonValid,
  polygonIssue,
  drawingPolygon,
  onCenter,
  onRadius,
  onSelectionMode,
  onStartDrawing,
  onFinishDrawing,
  onUndoPolygon,
  onClearPolygon,
  onBuild,
  importRecipe,
  recipeImportStatus,
  estimate,
  qualityPreset,
  onQualityPreset,
  verticalScale,
  onVerticalScale,
  demo,
}: {
  center: LatLng;
  radius: number;
  selectionMode: RegionSelection["mode"];
  polygonVertexCount: number;
  polygonAreaSquareMeters: number;
  polygonEnvelopeRadius: number;
  polygonValid: boolean;
  polygonIssue?: string | undefined;
  drawingPolygon: boolean;
  onCenter(center: LatLng): void;
  onRadius(radius: number): void;
  onSelectionMode(mode: RegionSelection["mode"]): void;
  onStartDrawing(): void;
  onFinishDrawing(): void;
  onUndoPolygon(): void;
  onClearPolygon(): void;
  onBuild(): void;
  importRecipe(file: File): Promise<void>;
  recipeImportStatus?: string | undefined;
  estimate: BuildEstimate;
  qualityPreset: QualityPreset;
  onQualityPreset(preset: QualityPreset): void;
  verticalScale: number;
  onVerticalScale(value: number): void;
  demo: boolean;
}) {
  const polygonWithinLimit = polygonVertexCount <= 256;
  const polygonReady = polygonVertexCount >= 3
    && polygonAreaSquareMeters > 0
    && polygonEnvelopeRadius <= 1000
    && polygonWithinLimit
    && polygonValid;
  const polygonStatus = polygonVertexCount < 3
    ? "Add at least 3 points"
    : !polygonWithinLimit
      ? "Boundary exceeds the 256-point limit"
      : polygonEnvelopeRadius > 1000
        ? "Area exceeds the 1 km limit"
        : polygonIssue === "duplicate-points"
          ? "Remove duplicate boundary points"
          : polygonIssue === "self-intersection"
            ? "Boundary edges cannot cross"
            : polygonIssue === "zero-area"
              ? "Boundary must enclose an area"
              : "Boundary ready";
  const selectedArea = selectionMode === "circle"
    ? Math.PI * radius * radius
    : polygonAreaSquareMeters;
  return (
    <aside className="selection-sidebar">
      <div className="sidebar-heading"><span className="success-mark"><Check /></span><div><strong>Sources connected</strong><small>{demo ? "Deterministic fixture ready" : "Keys validated for this session"}</small></div></div>
      <div className="compact-source-list">
        <div><span className="provider-g">G</span><span>Google Maps</span><CheckCircle2 /></div>
        <div><span className="provider-m">M</span><span>MapTiler</span><CheckCircle2 /></div>
      </div>
      <hr />
      <RecipeImportButton onImport={importRecipe} label="Open another recipe" />
      {recipeImportStatus && <p className="recipe-import-status" role="status">{recipeImportStatus}</p>}
      <section className="sidebar-section">
        <h2>Selection shape</h2>
        <div className="selection-mode-control" role="group" aria-label="Region selection shape">
          <button type="button" className={selectionMode === "circle" ? "selected" : ""} onClick={() => onSelectionMode("circle")}>
            <CircleDot size={16} /> Radius
          </button>
          <button type="button" className={selectionMode === "polygon" ? "selected" : ""} onClick={() => onSelectionMode("polygon")}>
            <PencilRuler size={16} /> Draw area
          </button>
        </div>
      </section>
      <section className="sidebar-section">
        <h2>Build quality</h2>
        <div className="quality-preset-control" role="group" aria-label="Build quality">
          {(["fast", "balanced", "detail"] as const).map((preset) => (
            <button
              type="button"
              key={preset}
              className={qualityPreset === preset ? "selected" : ""}
              onClick={() => onQualityPreset(preset)}
            >{preset}</button>
          ))}
        </div>
        <label className="vertical-scale-control">
          <span>Vertical scale <strong>{verticalScale.toFixed(1)}×</strong></span>
          <input
            aria-label="Vertical scale"
            type="range"
            min="0"
            max="3"
            step="0.1"
            value={verticalScale}
            onChange={(event) => onVerticalScale(event.target.valueAsNumber)}
          />
        </label>
      </section>
      <section className="sidebar-section center-section">
        <h2>{selectionMode === "circle" ? "Center" : "Area center"}</h2>
        <label htmlFor="latitude"><span>Latitude</span><div><input id="latitude" name="latitude" type="number" step="0.000001" value={center.lat} onChange={(event) => onCenter({ ...center, lat: clampCoordinate(event.target.valueAsNumber, -85.051128, 85.051128, center.lat) })} /><span>°</span></div></label>
        <label htmlFor="longitude"><span>Longitude</span><div><input id="longitude" name="longitude" type="number" step="0.000001" value={center.lng} onChange={(event) => onCenter({ ...center, lng: clampCoordinate(event.target.valueAsNumber, -180, 180, center.lng) })} /><span>°</span></div></label>
      </section>
      {selectionMode === "circle" ? (
        <section className="sidebar-section radius-section">
          <h2>Radius</h2>
          <input aria-label="Region radius" className="radius-slider" type="range" min="50" max="1000" step="10" value={radius} onChange={(event) => onRadius(event.target.valueAsNumber)} />
          <div className="range-labels"><span>50 m</span><strong>{Math.round(radius)} m</strong><span>1 km</span></div>
          <label className="radius-number" htmlFor="radius"><input id="radius" name="radius" type="number" min="50" max="1000" step="10" value={radius} onChange={(event) => onRadius(clampCoordinate(event.target.valueAsNumber, 50, 1000, radius))} /><span>m</span></label>
        </section>
      ) : (
        <section className="sidebar-section draw-section">
          <h2>Custom boundary</h2>
          <p>Click points around the zone. Terrain, buildings, roads and all other vector layers will be clipped to it.</p>
          <div className="draw-actions">
            {drawingPolygon ? (
              <button type="button" className="button secondary" onClick={onFinishDrawing} disabled={polygonVertexCount < 3}>
                <Check size={17} /> Finish area
              </button>
            ) : (
              <button type="button" className="button secondary" onClick={onStartDrawing}>
                <PencilRuler size={17} /> {polygonVertexCount >= 3 ? "Add more points" : "Start drawing"}
              </button>
            )}
            <button type="button" className="button text-button" onClick={onClearPolygon} disabled={polygonVertexCount === 0}>Clear</button>
            <button type="button" className="button text-button" onClick={onUndoPolygon} disabled={polygonVertexCount === 0}>Undo point</button>
          </div>
          <div className={`draw-status ${polygonReady ? "ready" : ""}`}>
            <span>{polygonVertexCount} point{polygonVertexCount === 1 ? "" : "s"}</span>
            <strong>{polygonStatus}</strong>
          </div>
        </section>
      )}
      <div className="selection-estimate">
        <span>Selected area</span>
        <strong>{(selectedArea / 1_000_000).toFixed(2)} km²</strong>
        <small>
          {selectionMode === "polygon" ? `Fetch envelope ${Math.round(polygonEnvelopeRadius)} m · ` : ""}
          z{estimate.imageryZoom} · {estimate.imageryTileCount} imagery tiles · {estimate.textureWidth}×{estimate.textureHeight}px
        </small>
        <small>
          ~{estimate.textureMemoryMegabytes.toFixed(0)} MB texture memory · {estimate.estimatedRequestCount} estimated tile requests
        </small>
        <small>
          ~{estimate.estimatedExportMegabytes.minimum}–{estimate.estimatedExportMegabytes.maximum} MB geometry export before optional compression
        </small>
      </div>
      <button className="button primary full build-button" onClick={onBuild} disabled={selectionMode === "polygon" && !polygonReady}><Box size={21} /> Build Three.js scene</button>
    </aside>
  );
}

function BuildScreen({ progress, error, onCancel, onBack }: { progress: BuildProgress | undefined; error: string | undefined; onCancel(): void; onBack(): void }) {
  const activeIndex = progress ? BUILD_STAGES.indexOf(progress.stage) : 0;
  const completedStages = new Set(progress?.completedStages);
  const activeStages = new Set(progress?.activeStages);
  const percent = Math.round((progress?.overallProgress ?? 0) * 100);
  return (
    <main className="build-layout">
      <section className="build-card">
        {error ? (
          <>
            <span className="build-state-icon error"><AlertTriangle /></span>
            <span className="section-kicker">Build stopped safely</span>
            <h1>Scene could not be completed</h1>
            <p className="build-error" role="alert">{error}</p>
            <button className="button primary" onClick={onBack}><ArrowLeft size={18} /> Back to region</button>
          </>
        ) : (
          <>
            <div className="progress-ring" style={{ "--progress": `${percent * 3.6}deg` } as React.CSSProperties}>
              <div><strong>{percent}%</strong><span>Building</span></div>
            </div>
            <span className="section-kicker">Cancellable local build</span>
            <h1>Constructing your map scene</h1>
            <p>{progress?.message ?? "Preparing build pipeline"}. Tiles remain in memory and are never added to the recipe.</p>
            <div className="build-stage-list">
              {BUILD_STAGES.map((stage, index) => {
                const complete = progress?.completedStages
                  ? completedStages.has(stage)
                  : index < activeIndex || (index === activeIndex && progress?.state === "completed");
                const active = progress?.activeStages
                  ? activeStages.has(stage)
                  : index === activeIndex && !complete;
                return <div className={`${complete ? "complete" : ""} ${active ? "active" : ""}`} key={stage}><span>{complete ? <Check /> : active ? <LoaderCircle className="spin" /> : index + 1}</span><strong>{BUILD_LABELS[stage]}</strong></div>;
              })}
            </div>
            <button className="button secondary" onClick={onCancel}><X size={18} /> Cancel build</button>
          </>
        )}
      </section>
      <div className="build-background" aria-hidden="true"><div className="wire-circle" /><div className="wire-grid" /></div>
    </main>
  );
}

function Toggle({ checked, onChange, label }: { checked: boolean; onChange(value: boolean): void; label: string }) {
  return <button type="button" role="switch" aria-checked={checked} aria-label={label} className={`toggle ${checked ? "on" : ""}`} onClick={() => onChange(!checked)}><span /></button>;
}

function LayerRow({ icon, label, checked, onChange }: { icon: React.ReactNode; label: string; checked: boolean; onChange(value: boolean): void }) {
  return <div className="layer-row"><span className="layer-icon">{icon}</span><span>{label}</span><Toggle label={`Toggle ${label}`} checked={checked} onChange={onChange} /></div>;
}

function ReviewScreen({
  build,
  recipe,
  layers,
  setLayers,
  cameraMode,
  setCameraMode,
  onBack,
  onSaveRecipe,
  onDownloadProject,
  onDownloadGlb,
  onDownloadNativeJson,
  onCameraPoseChange,
  onApplyCameraPreset,
  onImportGeoJson,
  overlayStatus,
  onImportEnrichment,
  enrichmentProposals,
  onEnrichmentDecision,
  exporting,
  exportError,
  exportMode,
  setExportMode,
  glbCompression,
  setGlbCompression,
}: {
  build: SceneBuildResult;
  recipe: SceneRecipe;
  layers: LayerVisibility;
  setLayers(value: LayerVisibility): void;
  cameraMode: "perspective" | "top";
  setCameraMode(mode: "perspective" | "top"): void;
  onBack(): void;
  onSaveRecipe(): void;
  onDownloadProject(): void;
  onDownloadGlb(): void;
  onDownloadNativeJson(): void;
  onCameraPoseChange(position: [number, number, number], target: [number, number, number]): void;
  onApplyCameraPreset(preset: CameraPreset): void;
  onImportGeoJson(file: File): Promise<void>;
  overlayStatus?: string | undefined;
  onImportEnrichment(file: File): Promise<void>;
  enrichmentProposals: EnrichmentReview[];
  onEnrichmentDecision(id: string, decision: "approved" | "rejected"): void;
  exporting?: SceneExportKind | undefined;
  exportError?: string | undefined;
  exportMode: "optimized" | "editable";
  setExportMode(mode: "optimized" | "editable"): void;
  glbCompression: GlbCompression;
  setGlbCompression(compression: GlbCompression): void;
}) {
  const copy = (value: string): void => { void navigator.clipboard?.writeText(value); };
  return (
    <main className="review-layout">
      <aside className="review-layers">
        <div className="review-ready"><span><Check /></span><div><strong>Scene ready</strong><small>Your scene is ready to review.</small></div></div>
        <hr />
        <h2>Scene view</h2>
        <div className="scene-view-control" role="group" aria-label="Scene view">
          <button
            type="button"
            className={layers.imagery ? "selected" : ""}
            aria-pressed={layers.imagery}
            onClick={() => setLayers({ ...layers, imagery: true })}
          >
            <Satellite size={16} /> Satellite
          </button>
          <button
            type="button"
            className={!layers.imagery ? "selected" : ""}
            aria-pressed={!layers.imagery}
            onClick={() => setLayers({ ...layers, imagery: false })}
          >
            <Box size={16} /> Geometry
          </button>
        </div>
        <p className="alignment-note">
          Vector footprints use ground coordinates. Satellite roofs may lean away from them because of aerial-image parallax.
        </p>
        <hr />
        <h2>Scene layers</h2>
        <LayerRow icon={<Image />} label="Ground imagery" checked={layers.imagery} onChange={(imagery) => setLayers({ ...layers, imagery })} />
        <LayerRow icon={<Mountain />} label="Terrain" checked={layers.terrain} onChange={(terrain) => setLayers({ ...layers, terrain })} />
        <LayerRow icon={<Trees />} label="Land surfaces" checked={layers.landcover} onChange={(landcover) => setLayers({ ...layers, landcover })} />
        <LayerRow icon={<Waves />} label="Water" checked={layers.water} onChange={(water) => setLayers({ ...layers, water })} />
        <LayerRow icon={<Route />} label="Roads" checked={layers.roads} onChange={(roads) => setLayers({ ...layers, roads })} />
        <LayerRow icon={<Box />} label="Underground x-ray" checked={layers.underground} onChange={(underground) => setLayers({ ...layers, underground })} />
        <LayerRow icon={<Fence />} label="Barriers & infrastructure" checked={layers.barriers} onChange={(barriers) => setLayers({ ...layers, barriers })} />
        <LayerRow icon={<MapPin />} label="Street details" checked={layers.streetObjects} onChange={(streetObjects) => setLayers({ ...layers, streetObjects })} />
        <LayerRow icon={<Building2 />} label="Buildings" checked={layers.buildings} onChange={(buildings) => setLayers({ ...layers, buildings })} />
        <LayerRow icon={<CircleDot />} label="Region boundary" checked={layers.boundary} onChange={(boundary) => setLayers({ ...layers, boundary })} />
        <div className="quality-summary">
          <Layers3 />
          <div><strong>{build.report.metrics.buildingFeatureCount} buildings · {build.report.metrics.roadFeatureCount} roads/bridges</strong><span>{build.report.metrics.tunnelFeatureCount} subsurface tunnels · {build.report.metrics.waterwayFeatureCount} waterways</span><span>{build.report.metrics.barrierFeatureCount + build.report.metrics.infrastructureFeatureCount} barriers/infrastructure · {build.report.metrics.streetObjectFeatureCount} street details</span><span>{build.report.metrics.waterFeatureCount + build.report.metrics.landcoverFeatureCount} surfaces · all rendered as Three.js geometry</span></div>
        </div>
      </aside>
      <Suspense fallback={<div className="viewport-loading"><LoaderCircle className="spin" /><span>Preparing Three.js viewport</span></div>}>
          <ThreeViewport
            build={build}
            center={recipe.center}
            radiusMeters={recipe.radiusMeters}
            cameraMode={cameraMode}
            layers={layers}
            attribution={build.report.attribution}
            initialCameraPosition={recipe.camera.position}
            initialCameraTarget={recipe.camera.target}
            onCameraChange={onCameraPoseChange}
          />
      </Suspense>
      <aside className="review-details">
        <button className="button secondary full" onClick={onBack}><ArrowLeft size={18} /> Back to region</button>
        <section className="export-actions" aria-labelledby="export-heading">
          <h2 id="export-heading">Export scene</h2>
          <label className="export-mode">
            <span>Geometry mode</span>
            <select value={exportMode} onChange={(event) => setExportMode(event.target.value as "optimized" | "editable")}>
              <option value="optimized">Optimized merged meshes</option>
              <option value="editable">Editable feature index</option>
            </select>
          </label>
          <label className="export-mode">
            <span>GLB compression</span>
            <select value={glbCompression} onChange={(event) => setGlbCompression(event.target.value as GlbCompression)}>
              <option value="none">Standard (widest compatibility)</option>
              <option value="meshopt">Meshopt (smaller download)</option>
            </select>
          </label>
          <button className="button primary full" onClick={onDownloadProject} disabled={Boolean(exporting)}>
            {exporting === "project" ? <LoaderCircle className="spin" size={18} /> : <Download size={18} />}
            {exporting === "project" ? "Preparing project…" : "Download Three.js project (.zip)"}
          </button>
          <button className="button secondary full" onClick={onDownloadGlb} disabled={Boolean(exporting)}>
            {exporting === "glb" ? <LoaderCircle className="spin" size={18} /> : <Download size={18} />}
            {exporting === "glb" ? "Preparing GLB…" : "Download GLB"}
          </button>
          <details className="advanced-export">
            <summary>Advanced export</summary>
            <button className="button secondary full" onClick={onDownloadNativeJson} disabled={Boolean(exporting)}>
              {exporting === "native-json" ? <LoaderCircle className="spin" size={18} /> : <Download size={18} />}
              {exporting === "native-json" ? "Preparing JSON…" : "Download native Three.js JSON"}
            </button>
          </details>
          <p className="export-note">The ZIP and GLB contain metric geometry and generated materials. Meshopt requires a compatible glTF loader. Provider satellite pixels and API credentials are never included.</p>
          {exportError && <p className="export-error" role="alert">{exportError}</p>}
        </section>
        <hr />
        <section className="recipe-download">
          <h2>Rebuild recipe</h2>
          <button className="button secondary full" onClick={onSaveRecipe} disabled={Boolean(exporting)}><Download size={18} /> Save scene recipe</button>
          <p className="export-note">Credential-free source settings for rebuilding from live providers; not a 3D asset.</p>
        </section>
        <section className="user-data-import">
          <h2>User data</h2>
          <RecipeImportButton onImport={onImportGeoJson} label="Add GeoJSON overlay" accept=".geojson,.json,application/geo+json,application/json" />
          <p className="export-note">User-owned points, lines and polygons are projected into metric scene coordinates and included in exports, but not the regeneration recipe.</p>
          {overlayStatus && <p className="recipe-import-status" role="status">{overlayStatus}</p>}
          <RecipeImportButton onImport={onImportEnrichment} label="Review AI enrichment manifest" />
          <p className="export-note">Only manifests derived from user-owned imagery are accepted. Every proposal shows confidence and requires approval.</p>
          {enrichmentProposals.length > 0 && <div className="enrichment-proposals">
            {enrichmentProposals.slice(0, 12).map((proposal) => (
              <article key={proposal.id} className={`enrichment-${proposal.decision}`}>
                <strong>{proposal.attribute.replaceAll("-", " ")}</strong>
                <span>{proposal.targetFeatureId} → {proposal.value}</span>
                <small>{Math.round(proposal.confidence * 100)}% confidence</small>
                {proposal.decision === "pending" ? <div>
                  <button onClick={() => onEnrichmentDecision(proposal.id, "approved")}>Approve</button>
                  <button onClick={() => onEnrichmentDecision(proposal.id, "rejected")}>Reject</button>
                </div> : <em>{proposal.decision}</em>}
              </article>
            ))}
          </div>}
        </section>
        <hr />
        <section>
          <h2>Scene recipe</h2>
          <h3>Center</h3>
          <div className="copy-field"><span>{recipe.center.lat.toFixed(6)}</span><button aria-label="Copy latitude" onClick={() => copy(recipe.center.lat.toFixed(6))}><Copy /></button></div>
          <div className="copy-field"><span>{recipe.center.lng.toFixed(6)}</span><button aria-label="Copy longitude" onClick={() => copy(recipe.center.lng.toFixed(6))}><Copy /></button></div>
          <dl className="recipe-facts"><div><dt>Region</dt><dd>{recipe.selection.mode === "circle" ? `${recipe.radiusMeters} m radius` : `${recipe.selection.vertices.length}-point area`}</dd></div><div><dt>World scale</dt><dd>1 unit = 1 m</dd></div><div><dt>Vertical scale</dt><dd>{recipe.verticalScale.toFixed(1)}×</dd></div></dl>
        </section>
        <section>
          <h2>Camera</h2>
          <div className="segmented-control" role="group" aria-label="Scene camera">
            <button className={cameraMode === "perspective" ? "selected" : ""} onClick={() => setCameraMode("perspective")}><Camera size={17} /> Perspective</button>
            <button className={cameraMode === "top" ? "selected" : ""} onClick={() => setCameraMode("top")}>Top</button>
          </div>
          <div className="camera-presets" aria-label="Camera presets">
            {(["isometric", "street", "topographic", "architectural"] as const).map((preset) => (
              <button type="button" key={preset} onClick={() => onApplyCameraPreset(preset)}>{preset}</button>
            ))}
          </div>
        </section>
        <hr />
        <section className="source-checks">
          <h2>Source checks</h2>
          {build.report.checks.map((check) => <div key={check.id} title={check.detail}>{check.status === "passed" ? <CheckCircle2 /> : <AlertTriangle />}<span>{check.label}</span></div>)}
        </section>
        <details className="limitations">
          <summary>Quality limitations ({build.report.warnings.length})</summary>
          <ul>{build.report.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>
        </details>
      </aside>
    </main>
  );
}

export function App() {
  const demo = useMemo(() => new URLSearchParams(window.location.search).get("demo") === "1", []);
  const [stage, setStage] = useState<AppStage>(1);
  const [googleKey, setGoogleKey] = useState("");
  const [mapTilerKey, setMapTilerKey] = useState("");
  const [connections, setConnections] = useState<ConnectionStatus>(INITIAL_CONNECTIONS);
  const [testing, setTesting] = useState(false);
  const [center, setCenter] = useState<LatLng>({ ...DEFAULT_SCENE_RECIPE.center });
  const [radius, setRadius] = useState(DEFAULT_SCENE_RECIPE.radiusMeters);
  const [selectionMode, setSelectionMode] = useState<RegionSelection["mode"]>("circle");
  const [polygonVertices, setPolygonVertices] = useState<LatLng[]>([]);
  const [drawingPolygon, setDrawingPolygon] = useState(false);
  const [progress, setProgress] = useState<BuildProgress | undefined>(undefined);
  const [buildError, setBuildError] = useState<string | undefined>(undefined);
  const [build, setBuild] = useState<SceneBuildResult | undefined>(undefined);
  const [sceneExporting, setSceneExporting] = useState<SceneExportKind | undefined>(undefined);
  const [sceneExportError, setSceneExportError] = useState<string | undefined>(undefined);
  const [exportMode, setExportMode] = useState<"optimized" | "editable">("optimized");
  const [glbCompression, setGlbCompression] = useState<GlbCompression>("none");
  const [cameraMode, setCameraMode] = useState<"perspective" | "top">("perspective");
  const [cameraPosition, setCameraPosition] = useState<[number, number, number] | undefined>(undefined);
  const [cameraTarget, setCameraTarget] = useState<[number, number, number] | undefined>(undefined);
  const [layers, setLayers] = useState<LayerVisibility>({ ...DEFAULT_SCENE_RECIPE.layers });
  const [qualityPreset, setQualityPreset] = useState<QualityPreset>("balanced");
  const [quality, setQuality] = useState<SceneRecipe["quality"]>({ ...DEFAULT_SCENE_RECIPE.quality });
  const [verticalScale, setVerticalScale] = useState(DEFAULT_SCENE_RECIPE.verticalScale);
  const [recipeImportStatus, setRecipeImportStatus] = useState<string | undefined>(undefined);
  const [overlayStatus, setOverlayStatus] = useState<string | undefined>(undefined);
  const [enrichmentProposals, setEnrichmentProposals] = useState<EnrichmentReview[]>([]);
  const abortRef = useRef<AbortController | undefined>(undefined);
  const buildRef = useRef<SceneBuildResult | undefined>(undefined);
  const validationAttemptRef = useRef(0);
  const [webGl2] = useState(detectWebGl2Capabilities);

  useEffect(() => {
    if (!webGl2.available || webGl2.maxTextureSize >= 4096) return;
    const supportedTextureSize = webGl2.maxTextureSize >= 2048 ? 2048 : 1024;
    setQualityPreset("fast");
    setQuality({
      ...QUALITY_PRESETS.fast,
      maxTextureSize: supportedTextureSize,
    });
  }, [webGl2.available, webGl2.maxTextureSize]);

  useEffect(() => {
    buildRef.current = build;
  }, [build]);

  useEffect(() => () => {
    abortRef.current?.abort();
    buildRef.current?.dispose();
  }, []);

  const recipe = useMemo<SceneRecipe>(() => ({
    ...DEFAULT_SCENE_RECIPE,
    center: { ...center },
    radiusMeters: radius,
    selection: selectionMode === "polygon"
      ? { mode: "polygon", vertices: polygonVertices.map((point) => ({ ...point })) }
      : { mode: "circle" },
    camera: {
      mode: cameraMode,
      ...(cameraPosition ? { position: cameraPosition } : {}),
      ...(cameraTarget ? { target: cameraTarget } : {}),
    },
    quality: { ...quality },
    verticalScale,
    layers: { ...layers },
  }), [cameraMode, cameraPosition, cameraTarget, center, layers, polygonVertices, quality, radius, selectionMode, verticalScale]);

  const polygonDetails = useMemo(() => polygonEnvelope(polygonVertices), [polygonVertices]);
  const polygonValidation = useMemo(() => validatePolygonSelection(polygonVertices), [polygonVertices]);
  const buildEstimate = useMemo(
    () => estimateBuild(
      center,
      selectionMode === "polygon" ? Math.max(50, polygonDetails?.radiusMeters ?? radius) : radius,
      quality,
    ),
    [center, polygonDetails?.radiusMeters, quality, radius, selectionMode],
  );

  const changeCenter = (nextCenter: LatLng): void => {
    if (selectionMode !== "polygon" || polygonVertices.length === 0) {
      setCenter(nextCenter);
      return;
    }
    const latitudeDelta = nextCenter.lat - center.lat;
    let longitudeDelta = nextCenter.lng - center.lng;
    if (longitudeDelta > 180) longitudeDelta -= 360;
    if (longitudeDelta < -180) longitudeDelta += 360;
    setPolygonVertices((vertices) => vertices.map((point) => {
      let lng = point.lng + longitudeDelta;
      if (lng > 180) lng -= 360;
      if (lng < -180) lng += 360;
      return { lat: point.lat + latitudeDelta, lng };
    }));
    setCenter(nextCenter);
  };

  const changePolygon = (vertices: LatLng[]): void => {
    setPolygonVertices(vertices);
    const envelope = polygonEnvelope(vertices);
    if (!envelope) return;
    setCenter(envelope.center);
    setRadius(Math.max(50, Math.min(1000, Math.ceil(envelope.radiusMeters))));
  };

  const importRecipe = async (file: File): Promise<void> => {
    try {
      const imported = migrateSceneRecipe(JSON.parse(await file.text()));
      buildRef.current?.dispose();
      buildRef.current = undefined;
      setBuild(undefined);
      setCenter({ ...imported.center });
      setRadius(imported.radiusMeters);
      setSelectionMode(imported.selection.mode);
      setPolygonVertices(imported.selection.mode === "polygon"
        ? imported.selection.vertices.map((point) => ({ ...point }))
        : []);
      setDrawingPolygon(false);
      setCameraMode(imported.camera.mode);
      setCameraPosition(imported.camera.position ? [...imported.camera.position] : undefined);
      setCameraTarget(imported.camera.target ? [...imported.camera.target] : undefined);
      setLayers({ ...imported.layers });
      setQuality({ ...imported.quality });
      setQualityPreset(
        imported.quality.targetImageryMetersPerPixel <= 0.25
          ? "detail"
          : imported.quality.maxTextureSize <= 2048
            ? "fast"
            : "balanced",
      );
      setVerticalScale(imported.verticalScale);
      setRecipeImportStatus(`Loaded ${file.name} · schema v${imported.schemaVersion}`);
      if (stage !== 1) setStage(2);
    } catch (error) {
      setRecipeImportStatus(`Could not open recipe: ${redactSensitiveText(error)}`);
    }
  };

  const importGeoJson = async (file: File): Promise<void> => {
    if (!build) return;
    try {
      const { importGeoJsonScene } = await import("@map2threejs/core/geojson-import");
      const overlay = importGeoJsonScene(JSON.parse(await file.text()), recipe.center);
      const previous = build.group.getObjectByName("user-overlays");
      previous?.traverse((object) => {
        const drawable = object as unknown as {
          geometry?: { dispose(): void };
          material?: { dispose(): void } | Array<{ dispose(): void }>;
        };
        drawable.geometry?.dispose();
        const materials = Array.isArray(drawable.material) ? drawable.material : drawable.material ? [drawable.material] : [];
        for (const material of materials) material.dispose();
      });
      previous?.removeFromParent();
      build.group.add(overlay);
      build.bounds.expandByObject(overlay);
      setOverlayStatus(`Added ${overlay.children.length} GeoJSON object(s) from ${file.name}`);
    } catch (error) {
      setOverlayStatus(`Could not add GeoJSON: ${redactSensitiveText(error)}`);
    }
  };

  const importEnrichment = async (file: File): Promise<void> => {
    try {
      const { parseSceneEnrichmentManifest } = await import("@map2threejs/core/enrichment");
      const manifest = parseSceneEnrichmentManifest(JSON.parse(await file.text()));
      setEnrichmentProposals(manifest.proposals.map((proposal) => ({ ...proposal, decision: "pending" })));
      setOverlayStatus(`Loaded ${manifest.proposals.length} AI enrichment proposal(s) for manual review`);
    } catch (error) {
      setOverlayStatus(`Could not open enrichment manifest: ${redactSensitiveText(error)}`);
    }
  };

  const decideEnrichment = async (
    id: string,
    decision: "approved" | "rejected",
  ): Promise<void> => {
    const proposal = enrichmentProposals.find((candidate) => candidate.id === id);
    if (!proposal || !build) return;
    if (decision === "approved") {
      const { applyApprovedSceneEnrichments } = await import("@map2threejs/core/enrichment");
      build.features = applyApprovedSceneEnrichments(build.group, [proposal]);
    }
    setEnrichmentProposals((current) => current.map((candidate) =>
      candidate.id === id ? { ...candidate, decision } : candidate));
  };

  const testConnections = async (): Promise<void> => {
    const attempt = ++validationAttemptRef.current;
    setTesting(true);
    setConnections({ google: "testing", maptiler: "testing" });
    if (demo) {
      await new Promise((resolve) => globalThis.setTimeout(resolve, 350));
      if (attempt !== validationAttemptRef.current) return;
      setConnections({ google: "connected", maptiler: "connected" });
      setTesting(false);
      return;
    }
    const controller = new AbortController();
    const googleTest = loadGoogleMaps(googleKey).then(async (googleApi) => {
      await googleApi.maps.importLibrary("places");
      return googleApi;
    });
    const provider = new MapTilerProvider(mapTilerKey);
    const mapTilerTest = provider.validate(controller.signal);
    const [googleResult, mapTilerResult] = await Promise.allSettled([googleTest, mapTilerTest]);
    if (attempt !== validationAttemptRef.current) return;
    provider.dispose();
    setConnections({
      google: googleResult.status === "fulfilled" ? "connected" : "error",
      maptiler: mapTilerResult.status === "fulfilled" ? "connected" : "error",
      ...(googleResult.status === "rejected" ? { googleError: redactSensitiveText(googleResult.reason) } : {}),
      ...(mapTilerResult.status === "rejected" ? { maptilerError: redactSensitiveText(mapTilerResult.reason) } : {}),
    });
    setTesting(false);
  };

  const changeGoogleKey = (value: string): void => {
    validationAttemptRef.current += 1;
    setTesting(false);
    setGoogleKey(value);
    setConnections((current) => {
      const { googleError: _googleError, ...rest } = current;
      return { ...rest, google: "idle" };
    });
  };

  const changeMapTilerKey = (value: string): void => {
    validationAttemptRef.current += 1;
    setTesting(false);
    setMapTilerKey(value);
    setConnections((current) => {
      const { maptilerError: _maptilerError, ...rest } = current;
      return { ...rest, maptiler: "idle" };
    });
  };

  const startBuild = async (): Promise<void> => {
    if (
      selectionMode === "polygon"
      && (!polygonDetails || polygonDetails.radiusMeters > 1000 || !polygonValidation.valid)
    ) return;
    setDrawingPolygon(false);
    buildRef.current?.dispose();
    setBuild(undefined);
    setBuildError(undefined);
    setProgress(undefined);
    setStage(3);
    const controller = new AbortController();
    abortRef.current = controller;
    const provider = demo ? new DemoMapProvider() : new MapTilerProvider(mapTilerKey);
    try {
      const { buildMapScene } = await import("@map2threejs/core/scene");
      const nextBuild = await buildMapScene(recipe, provider, { signal: controller.signal, onProgress: setProgress });
      if (controller.signal.aborted) {
        nextBuild.dispose();
        return;
      }
      setBuild(nextBuild);
      if (recipe.selection.mode === "polygon") {
        setCameraMode("top");
        setCameraPosition(undefined);
        setCameraTarget(undefined);
      }
      setStage(4);
    } catch (error) {
      if (controller.signal.aborted || error instanceof BuildCancelledError) {
        setStage(2);
      } else {
        setBuildError(redactSensitiveText(error));
      }
    } finally {
      if (provider instanceof MapTilerProvider) provider.dispose();
      abortRef.current = undefined;
    }
  };

  const backToRegion = (): void => {
    buildRef.current?.dispose();
    buildRef.current = undefined;
    setBuild(undefined);
    setStage(2);
  };

  const saveRecipe = (): void => {
    const blob = new Blob([serializeSceneRecipe(recipe)], { type: "application/json" });
    downloadBlob(
      blob,
      `${exportBaseName(recipe)}-${recipe.selection.mode === "circle" ? `${recipe.radiusMeters}m` : "custom-area"}.recipe.json`,
    );
  };

  const exportScene = async (kind: SceneExportKind): Promise<void> => {
    if (!build) return;
    setSceneExporting(kind);
    setSceneExportError(undefined);
    try {
      const exportRecipe = recipe;
      const options = { recipe: exportRecipe, attribution: build.report.attribution, mode: exportMode };
      const baseName = exportBaseName(recipe);
      if (kind === "project") {
        const { exportThreeSceneGlbInWorker } = await import("@map2threejs/core/three-export-worker-client");
        const { createThreeProjectZip } = await import("@map2threejs/core/three-project");
        const viewerResponse = await fetch(`${import.meta.env.BASE_URL}standalone-viewer.js`);
        if (!viewerResponse.ok) {
          throw new Error(`Could not prepare the offline viewer (${viewerResponse.status}).`);
        }
        const projectGlb = await exportThreeSceneGlbInWorker(build.group, {
          ...options,
          compression: "none",
        });
        const archive = await createThreeProjectZip(build.group, {
          ...options,
          standaloneViewerJavaScript: await viewerResponse.text(),
        }, projectGlb);
        const owned = new Uint8Array(archive.byteLength);
        owned.set(archive);
        downloadBlob(new Blob([owned.buffer], { type: "application/zip" }), `${baseName}-threejs-project.zip`);
      } else if (kind === "glb") {
        const { exportThreeSceneGlbInWorker } = await import("@map2threejs/core/three-export-worker-client");
        const glb = await exportThreeSceneGlbInWorker(build.group, {
          ...options,
          compression: glbCompression,
        });
        downloadBlob(new Blob([glb], { type: "model/gltf-binary" }), `${baseName}.glb`);
      } else {
        const { serializeThreeScene } = await import("@map2threejs/core/three-export");
        const contents = serializeThreeScene(build.group, options);
        downloadBlob(new Blob([contents], { type: "application/json" }), `${baseName}.three.json`);
      }
    } catch (error) {
      setSceneExportError(redactSensitiveText(error));
    } finally {
      setSceneExporting(undefined);
    }
  };

  if (!webGl2.available || webGl2.maxTextureSize < 1024) {
    return (
      <div className="blocking-state">
        <span><AlertTriangle /></span>
        <h1>{webGl2.available ? "A larger WebGL texture limit is required" : "WebGL2 is required"}</h1>
        <p>{webGl2.available
          ? `This graphics device supports textures up to ${webGl2.maxTextureSize}px, while map2threejs requires at least 1024px textures. Use a device with a larger GPU texture limit.`
          : "map2threejs needs a browser and graphics driver with WebGL2 enabled to build and review a Three.js map scene."}</p>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <WorkflowHeader activeStep={stage} demo={demo} />
      {stage === 1 && <ConnectScreen demo={demo} googleKey={googleKey} mapTilerKey={mapTilerKey} setGoogleKey={changeGoogleKey} setMapTilerKey={changeMapTilerKey} connections={connections} testing={testing} testConnections={() => { void testConnections(); }} continueToRegion={() => setStage(2)} importRecipe={importRecipe} recipeImportStatus={recipeImportStatus} />}
      {stage === 2 && (
        <main className="selection-layout">
          <SelectionSidebar
            center={center}
            radius={radius}
            selectionMode={selectionMode}
            polygonVertexCount={polygonVertices.length}
            polygonAreaSquareMeters={polygonDetails?.areaSquareMeters ?? 0}
            polygonEnvelopeRadius={polygonDetails?.radiusMeters ?? 0}
            polygonValid={polygonValidation.valid}
            polygonIssue={polygonValidation.issues[0]}
            drawingPolygon={drawingPolygon}
            onCenter={changeCenter}
            onRadius={setRadius}
            onSelectionMode={(mode) => {
              setSelectionMode(mode);
              setDrawingPolygon(mode === "polygon" && polygonVertices.length < 3);
            }}
            onStartDrawing={() => setDrawingPolygon(true)}
            onFinishDrawing={() => setDrawingPolygon(false)}
            onUndoPolygon={() => setPolygonVertices((vertices) => vertices.slice(0, -1))}
            onClearPolygon={() => {
              setPolygonVertices([]);
              setDrawingPolygon(true);
            }}
            onBuild={() => { void startBuild(); }}
            importRecipe={importRecipe}
            recipeImportStatus={recipeImportStatus}
            estimate={buildEstimate}
            qualityPreset={qualityPreset}
            onQualityPreset={(preset) => {
              setQualityPreset(preset);
              setQuality({ ...QUALITY_PRESETS[preset] });
            }}
            verticalScale={verticalScale}
            onVerticalScale={setVerticalScale}
            demo={demo}
          />
          {demo ? (
            <DemoSelector
              center={center}
              radiusMeters={radius}
              selectionMode={selectionMode}
              polygonVertices={polygonVertices}
              drawingPolygon={drawingPolygon}
              onCircleChange={(nextCenter, nextRadius) => {
                setCenter(nextCenter);
                setRadius(Math.round(nextRadius));
              }}
              onPolygonChange={changePolygon}
              onSearchCenter={changeCenter}
              onFinishDrawing={() => setDrawingPolygon(false)}
            />
          ) : (
            <GoogleSelector
              apiKey={googleKey}
              center={center}
              radiusMeters={radius}
              selectionMode={selectionMode}
              polygonVertices={polygonVertices}
              drawingPolygon={drawingPolygon}
              onCircleChange={(nextCenter, nextRadius) => {
                setCenter(nextCenter);
                setRadius(Math.round(nextRadius));
              }}
              onPolygonChange={changePolygon}
              onSearchCenter={changeCenter}
              onFinishDrawing={() => setDrawingPolygon(false)}
            />
          )}
        </main>
      )}
      {stage === 3 && <BuildScreen progress={progress} error={buildError} onCancel={() => abortRef.current?.abort()} onBack={() => setStage(2)} />}
      {stage === 4 && build && (
        <ReviewScreen
          build={build}
          recipe={recipe}
          layers={layers}
          setLayers={setLayers}
          cameraMode={cameraMode}
          setCameraMode={setCameraMode}
          onBack={backToRegion}
          onSaveRecipe={saveRecipe}
          onDownloadProject={() => { void exportScene("project"); }}
          onDownloadGlb={() => { void exportScene("glb"); }}
          onDownloadNativeJson={() => { void exportScene("native-json"); }}
          onCameraPoseChange={(position, target) => {
            setCameraPosition(position);
            setCameraTarget(target);
          }}
          onApplyCameraPreset={(preset) => {
            const extent = recipe.radiusMeters;
            const target: [number, number, number] = [0, Math.max(0, build.bounds.min.y), 0];
            if (preset === "topographic") {
              setCameraMode("top");
              setCameraPosition([0, extent * 3, 0]);
            } else {
              setCameraMode("perspective");
              setCameraPosition(preset === "street"
                ? [0, build.bounds.min.y + 2.2, extent * 0.85]
                : preset === "architectural"
                  ? [extent * 0.55, build.bounds.max.y + extent * 0.35, extent * 0.55]
                  : [extent * 0.9, build.bounds.max.y + extent * 0.7, extent * 0.9]);
            }
            setCameraTarget(target);
          }}
          onImportGeoJson={importGeoJson}
          overlayStatus={overlayStatus}
          onImportEnrichment={importEnrichment}
          enrichmentProposals={enrichmentProposals}
          onEnrichmentDecision={(id, decision) => { void decideEnrichment(id, decision); }}
          exporting={sceneExporting}
          exportError={sceneExportError}
          exportMode={exportMode}
          setExportMode={setExportMode}
          glbCompression={glbCompression}
          setGlbCompression={setGlbCompression}
        />
      )}
      <div className="desktop-required" role="dialog" aria-modal="true" aria-labelledby="desktop-required-title">
        <Box />
        <h1 id="desktop-required-title">Desktop workspace required</h1>
        <p>map2threejs needs at least 900 px of viewport width for map selection and scene review.</p>
      </div>
    </div>
  );
}
